import { useApps } from '@/hooks/useApps'
import { SectionHeader, Card, Empty, Skeleton } from '@/components/ui'
import { Briefcase, ChevronRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { formatCurrency, entityColor, cn } from '@/lib/utils'
import type { AcquisitionStage } from '@/lib/types'

const STAGES: { value: AcquisitionStage; label: string; description: string }[] = [
  { value: 'lead',          label: 'Lead',           description: 'First contact, qualifying interest' },
  { value: 'qualified',     label: 'Qualified',      description: 'Authority + budget confirmed' },
  { value: 'due-diligence', label: 'Due Diligence',  description: 'Buyer reviewing code, terms, risks' },
  { value: 'loi',           label: 'LOI',            description: 'Letter of Intent on the table' },
  { value: 'closing',       label: 'Closing',        description: 'Wire transfer and assignment in motion' },
]

export function PipelinePage() {
  const { data: apps, isLoading } = useApps()

  if (isLoading) return <Skeleton className="h-96" />

  const inPipeline = (apps ?? []).filter((a) =>
    a.acquisition && a.acquisition.stage !== 'not-listed' && a.acquisition.stage !== 'closed-won' && a.acquisition.stage !== 'closed-lost'
  )

  const totalAsk = inPipeline.reduce((sum, a) => sum + ((a.acquisition as Record<string, unknown>)?.ask_price as number ?? a.valuation?.acquisition.midpoint ?? 0), 0)
  const totalOffered = inPipeline.reduce((sum, a) => sum + ((a.acquisition as Record<string, unknown>)?.offered_price as number ?? 0), 0)

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Pipeline"
        title={`${inPipeline.length} active ${inPipeline.length === 1 ? 'opportunity' : 'opportunities'}`}
        description={`${formatCurrency(totalAsk, { compact: true })} ask · ${formatCurrency(totalOffered, { compact: true })} offered`}
      />

      {inPipeline.length === 0 ? (
        <Empty
          icon={<Briefcase size={48} />}
          title="Pipeline is empty"
          description="Open an app, set its acquisition stage, and it will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 min-h-[400px]">
          {STAGES.map((stage) => {
            const stageApps = inPipeline.filter((a) => a.acquisition?.stage === stage.value)
            return (
              <div key={stage.value} className="space-y-3">
                <div className="border-b border-bronze/20 pb-3">
                  <div className="eyebrow mb-1">{stage.label}</div>
                  <div className="font-mono text-[10px] text-cream/55">{stage.description}</div>
                  <div className="font-mono text-[10px] text-gold-soft mt-1">{stageApps.length} {stageApps.length === 1 ? 'app' : 'apps'}</div>
                </div>
                <div className="space-y-2">
                  {stageApps.map((a) => {
                    const eC = entityColor(a.entity)
                    const ask = (a.acquisition as Record<string, unknown> | undefined)?.ask_price as number | undefined
                    const offered = (a.acquisition as Record<string, unknown> | undefined)?.offered_price as number | undefined
                    return (
                      <Link
                        key={a.slug}
                        to="/apps/$slug"
                        params={{ slug: a.slug }}
                        className="block group"
                      >
                        <Card className="!p-3 group-hover:border-gold/40 transition-colors">
                          <div className={cn('eyebrow mb-1.5', eC.text)}>{a.entity}</div>
                          <div className="font-heading text-sm text-cream group-hover:text-gold-soft transition-colors mb-2">{a.name}</div>
                          {ask && (
                            <div className="font-mono text-xs text-gold-soft">Ask {formatCurrency(ask, { compact: true })}</div>
                          )}
                          {offered && (
                            <div className="font-mono text-xs text-success">Offered {formatCurrency(offered, { compact: true })}</div>
                          )}
                          {(() => {
                            const bc = (a.acquisition as Record<string, unknown> | undefined)?.buyer_company
                            return bc ? (
                              <div className="font-mono text-[10px] text-cream/55 mt-1.5 flex items-center gap-1">
                                <ChevronRight size={10} />
                                {String(bc)}
                              </div>
                            ) : null
                          })()}
                        </Card>
                      </Link>
                    )
                  })}
                  {stageApps.length === 0 && (
                    <div className="border border-dashed border-cream/10 rounded-md p-4 text-center">
                      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/35">empty</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Card className="border-success/20 bg-success/5">
        <div className="flex gap-3">
          <Briefcase size={18} className="text-success shrink-0 mt-0.5" />
          <div>
            <div className="font-heading text-base text-cream mb-1">Legal posture · counsel-reviewed</div>
            <p className="font-body text-sm text-cream/75 leading-relaxed">
              Per counsel review, the personal Chapter 7 is understood to have been
              <strong className="text-success"> resolved as a no-asset case</strong>, and there is no
              currently identified blocker to transacting ARH, FTM, TSA, or DRIJEZIE-bucket assets —
              subject to counsel confirmation at signing. For any RRM-touching deal, confirm the
              current legal posture with counsel before signing an LOI to keep the buyer informed
              and the record clean. This summary is not legal advice and does not substitute for
              counsel's sign-off on a specific transaction.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
